import React, { useEffect, useState } from "react";
import {
  Loader2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Gift,
  Users,
  Sun,
  Receipt,
  Inbox,
} from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

function getActivityMeta(type) {
  const t = String(type || "").toLowerCase();

  if (t.includes("withdraw")) {
    return { icon: ArrowUpFromLine, bg: "bg-[#E5566B]/10", color: "text-[#E5566B]" };
  }
  if (t.includes("deposit")) {
    return { icon: ArrowDownToLine, bg: "bg-[#3FBF8F]/10", color: "text-[#3FBF8F]" };
  }
  if (t.includes("referral")) {
    return { icon: Users, bg: "bg-[#8B5CF6]/10", color: "text-[#B79CFF]" };
  }
  if (t.includes("daily") || t.includes("bonus") || t.includes("earning")) {
    return { icon: Sun, bg: "bg-[#F5B942]/10", color: "text-[#F5B942]" };
  }
  if (t.includes("vip") || t.includes("unlock")) {
    return { icon: Gift, bg: "bg-[#5B8DEF]/10", color: "text-[#6E93FF]" };
  }
  return { icon: Receipt, bg: "bg-[#7C8AA5]/10", color: "text-[#7C8AA5]" };
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (sameDay) {
    return "Today, " + time;
  }

  return (
    d.toLocaleDateString([], { month: "short", day: "numeric" }) + ", " + time
  );
}

export default function Transactions() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(function () {
    const loadActivities = async function () {
      try {
        setLoading(true);
        setError("");

        const data = await apiRequest("/users/me/points-log");

        setActivities(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to load activities.");
      } finally {
        setLoading(false);
      }
    };

    loadActivities();
  }, []);

  return (
    <Layout title="Activities">
      <div className="max-w-3xl mx-auto space-y-5">
        <div>
          <h1 className="text-lg font-bold text-white">All Activities</h1>
          <p className="text-xs text-[#7C8AA5] mt-1">
            A full history of your deposits, withdrawals and earnings.
          </p>
        </div>

        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-4 sm:p-6">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(function (i) {
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 py-3 border-b border-[#161B2C] last:border-0 animate-pulse"
                  >
                    <div className="h-10 w-10 rounded-full bg-[#161B2C]" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-1/3 rounded bg-[#161B2C]" />
                      <div className="h-2 w-1/4 rounded bg-[#161B2C]" />
                    </div>
                    <div className="h-3 w-14 rounded bg-[#161B2C]" />
                  </div>
                );
              })}
            </div>
          ) : error ? (
            <div className="rounded-xl bg-[#E5566B]/10 border border-[#E5566B]/20 text-[#E5566B] text-sm px-4 py-3">
              {error}
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#161B2C] mb-4">
                <Inbox size={24} className="text-[#5D6880]" />
              </div>
              <div className="text-sm font-medium text-white mb-1">
                No activity yet
              </div>
              <div className="text-xs text-[#7C8AA5] max-w-[220px]">
                Your deposits, withdrawals and earnings will show up here.
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {activities.map(function (tx) {
                const amount = Number(tx.amount ?? tx.amount_usdt ?? 0);
                const positive = amount >= 0;
                const meta = getActivityMeta(tx.type);
                const Icon = meta.icon;

                return (
                  <div
                    key={tx.id}
                    className="flex items-center gap-3 py-3 border-b border-[#161B2C] last:border-0"
                  >
                    <div
                      className={
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full " +
                        meta.bg +
                        " " +
                        meta.color
                      }
                    >
                      <Icon size={17} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium capitalize truncate text-white">
                        {String(tx.type || "Activity").replace(/_/g, " ")}
                      </div>
                      <div className="text-[11px] text-[#7C8AA5] truncate">
                        {formatDate(tx.created_at)}
                        {tx.note ? " · " + tx.note : ""}
                      </div>
                    </div>

                    <div
                      className={
                        "text-sm font-semibold whitespace-nowrap " +
                        (positive ? "text-[#3FBF8F]" : "text-[#E5566B]")
                      }
                    >
                      {amount > 0 ? "+" : ""}
                      {amount.toFixed(2)} USDT
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}